import React, { Dispatch, ReactNode, SetStateAction, useRef } from 'react';

import { ChevronLeft, ChevronRight, Close, Refresh } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { BoxResizeHandle } from '../../common/components/BoxResizeHandle';

const SIDE_PANEL_DETAIL_DEFAULT_Z_INDEX = 1210;

interface SidePanelDetailProps {
	title: string;
	subtitle?: ReactNode;
	isLoading: boolean;
	isFetching?: boolean;
	error?: Error | null;
	errorMessage?: string;
	zIndex?: number;
	top?: number;
	bottom?: number;
	rightOffset?: number;
	width?: number;
	collapsedWidth?: number;
	isCollapsed?: boolean;
	positioning?: 'fixed' | 'absolute' | 'none'; // 'fixed' for viewport (default), 'absolute' for parent container, 'none' for no positioning override
	position?: 'stacked' | 'standalone'; // 'stacked' for left of another panel, 'standalone' for far right
	listPanelCollapsed?: boolean; // Whether the list panel is collapsed
	listPanelCollapsedWidth?: number; // Width of collapsed list panel
	isFullWidth?: boolean;
	canClose?: boolean; // Whether the close button is visible (default: true)
	onClose?: () => void;
	onRefresh?: () => void;
	onToggleCollapse?: () => void;
	children: ReactNode;
	headerActions?: ReactNode;
	resizeStorageKey?: string;
	defaultWidth?: number;
	minWidth?: number;
	maxWidth?: number;
	setWidth?: Dispatch<SetStateAction<number>>;
}

export default function SidePanelDetail({
	title,
	subtitle,
	isLoading,
	isFetching = false,
	error,
	errorMessage = 'Failed to load details',
	zIndex = SIDE_PANEL_DETAIL_DEFAULT_Z_INDEX,
	top = 0,
	bottom = 0,
	rightOffset = 400,
	width = 400,
	collapsedWidth = 48,
	isCollapsed = false,
	positioning = 'fixed',
	position = 'stacked',
	listPanelCollapsed = false,
	listPanelCollapsedWidth = 48,
	isFullWidth = false,
	canClose = true,
	onClose,
	onRefresh,
	onToggleCollapse,
	children,
	headerActions,
	resizeStorageKey,
	defaultWidth,
	minWidth,
	maxWidth,
	setWidth,
}: SidePanelDetailProps) {
	const panelRef = useRef<HTMLDivElement>(null);
	// Auto-calculate defaults based on position and collapsed state
	const effectiveZIndex = zIndex !== SIDE_PANEL_DETAIL_DEFAULT_Z_INDEX ? zIndex : position === 'standalone' ? 1200 : SIDE_PANEL_DETAIL_DEFAULT_Z_INDEX;
	// Adjust right offset when list panel is collapsed
	let effectiveRightOffset = rightOffset !== 400 ? rightOffset : position === 'standalone' ? 0 : 400;
	if (listPanelCollapsed && position === 'stacked') {
		effectiveRightOffset = listPanelCollapsedWidth;
	}
	if (isFullWidth) {
		effectiveRightOffset = 0;
	}
	const stackedPanelOverlap = position === 'stacked' ? 1 : 0;
	const effectiveWidth = isCollapsed ? collapsedWidth : isFullWidth ? '100vw' : width;
	const canResize = !!resizeStorageKey && !!setWidth && !isCollapsed && !isFullWidth;

	return (
		<Paper
			ref={panelRef}
			className="responsive-side-panel"
			sx={{
				...(positioning !== 'none' && {
					position: positioning,
					top,
					right: isFullWidth ? 0 : effectiveRightOffset - stackedPanelOverlap,
					bottom,
					zIndex: effectiveZIndex,
				}),
				width: effectiveWidth,
				display: 'flex',
				flexDirection: 'column',
				border: '1px solid',
				borderColor: (theme) => alpha(theme.palette.divider, 0.45),
				backgroundColor: 'background.paper',
				borderRadius: isFullWidth ? 0 : '8px 0 0 8px',
				boxShadow: position === 'stacked' ? '10px 12px 28px rgba(0, 0, 0, 0.32)' : '-10px 12px 28px rgba(0, 0, 0, 0.28)',
				transition: canResize ? 'right 0.3s ease-in-out' : 'width 0.3s ease-in-out, right 0.3s ease-in-out',
				overflowX: 'hidden',
			}}
		>
			{canResize && (
				<BoxResizeHandle
					position="left"
					width={width}
					setWidth={setWidth}
					panelRef={panelRef}
					storageKey={resizeStorageKey}
					defaultWidth={defaultWidth ?? width}
					minWidth={minWidth ?? 200}
					maxWidth={maxWidth ?? 800}
					direction="right"
				/>
			)}

			{/* Header */}
			<Box
				sx={{
					p: 2,
					backgroundColor: 'action.hover',
					borderBottom: '1px solid',
					borderColor: (theme) => alpha(theme.palette.divider, 0.55),
					minHeight: isCollapsed ? 'auto' : undefined,
				}}
			>
				{isCollapsed ? (
					<Stack direction="column" spacing={1} alignItems="center">
						{onToggleCollapse && (
							<IconButton size="small" onClick={onToggleCollapse} title="Expand panel">
								<ChevronLeft />
							</IconButton>
						)}
						{onRefresh && (
							<IconButton size="small" onClick={onRefresh} title="Refresh">
								<Refresh
									sx={{
										animation: isFetching ? 'spin 1s linear infinite' : 'none',
										'@keyframes spin': {
											'0%': { transform: 'rotate(0deg)' },
											'100%': { transform: 'rotate(360deg)' },
										},
									}}
								/>
							</IconButton>
						)}
						{canClose && (
							<IconButton size="small" onClick={onClose} title="Close panel">
								<Close />
							</IconButton>
						)}
					</Stack>
				) : (
					<>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Typography variant="h6" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{title}
							</Typography>
							<Stack direction="row" spacing={1}>
								{headerActions}
								{onToggleCollapse && (
									<IconButton size="small" onClick={onToggleCollapse} title="Collapse panel">
										<ChevronRight />
									</IconButton>
								)}
								{onRefresh && (
									<IconButton size="small" onClick={onRefresh} title="Refresh">
										<Refresh
											sx={{
												animation: isFetching ? 'spin 1s linear infinite' : 'none',
												'@keyframes spin': {
													'0%': { transform: 'rotate(0deg)' },
													'100%': { transform: 'rotate(360deg)' },
												},
											}}
										/>
									</IconButton>
								)}
								{canClose && (
									<IconButton size="small" onClick={onClose} title="Close panel">
										<Close />
									</IconButton>
								)}
							</Stack>
						</Stack>
						{subtitle && (
							<Box sx={{ mt: 0.5 }}>
								{typeof subtitle === 'string' ? (
									<Typography variant="body2" color="textSecondary">
										{subtitle}
									</Typography>
								) : (
									subtitle
								)}
							</Box>
						)}
					</>
				)}
			</Box>

			{/* Content */}
			{!isCollapsed && (
				<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
					{isLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
							<CircularProgress />
						</Box>
					)}

					{error && (
						<Box sx={{ p: 2 }}>
							<Alert severity="error">
								{errorMessage}: {error instanceof Error ? error.message : 'Unknown error'}
							</Alert>
						</Box>
					)}

					{!isLoading && !error && <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: 'background.default' }}>{children}</Box>}
				</Box>
			)}
		</Paper>
	);
}
